/**
 * Turning the details form into a shipment and sending the user to its quotes.
 *
 * Three things make this more than one API call. An anonymous user has to sign
 * in first, so the button awaits a dialog before it can do anything. The
 * create-shipment request is slow, so the button spins and cycles a message
 * while it runs. And it's slow enough to be rage-clicked, so a ref guards it —
 * state alone re-renders too late to stop the second click.
 */

import { useShipmentService2 } from "@/Services/ShipmentService2";
import type { UUID } from "@/Shared/types";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { AuthenticateForQuotesDialogBody } from "../Dialogs/Internal/AuthenticateForQuotesDialogBody";
import type { BookShipmentShipmentType } from "@/Shared/Types/bookShipmentSliceTypes";
import { linkShipmentDraft } from "@/Slices/bookShipmentSlice";
import { useBookShipmentRotatingMessage } from "./useBookShipmentRotatingMessage";

// Rotating progress messages shown inside the "Get instant quotes" button
// while the slow create-shipment request runs. Reassures the user we're
// working and, with the submit guard, further discourages rage clicks. Kept
// close in length so the button's width barely shifts as they cycle.
const QUOTE_PREPARATION_MESSAGES = [
  "Searching among carriers…",
  "Finding you the best price…",
  "Checking transit times…",
  "Building your quote list…",
];

// If the dimensions were entered in cm/kg
// then perhaps we have a lot of decimal places laying around
// so need to round up to the nearest in/lb
function roundUpDimension(value: number | undefined) {
  if (value === undefined) {
    return value;
  }

  return Math.ceil(value);
}

function roundDimensions(lineItem: AddLineItemType): AddLineItemType {
  // Basically overwrite the dimensions with rounded versions
  return {
    ...lineItem,
    length: roundUpDimension(lineItem.length),
    width: roundUpDimension(lineItem.width),
    height: roundUpDimension(lineItem.height),
    weightPerHandlingUnit: roundUpDimension(lineItem.weightPerHandlingUnit),
  };
}

interface BookShipmentGetQuotesInput {
  shipment: BookShipmentShipmentType;
  shipmentDraftId: UUID | undefined;
  replacesId: UUID | undefined;
  isValid: boolean;
  /** Called when the user tries to continue with a form that can't be quoted. */
  onValidationFailed: () => void;
}

export function useBookShipmentGetQuotes(input: BookShipmentGetQuotesInput) {
  const { shipment, shipmentDraftId, replacesId, isValid, onValidationFailed } =
    input;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuthentication();
  const shipmentService = useShipmentService2();

  const [submittingQuotes, setSubmittingQuotes] = useState(false);
  const [quotesRequested, setQuotesRequested] = useState(false);
  const [quoteRequestError, setQuoteRequestError] = useState(false);
  const isSubmittingQuotesRef = useRef(false);

  // Autocomplete in Chrome when creating an account can interfere with the entered
  // postal codes - so we want to track when the create account popup is showing
  // and disable postal code inputs
  const [showingCreateAccountPopup, setShowingCreateAccountPopup] =
    useState(false);

  // The authenticate dialog resolves the promise the get-quotes handler
  // awaits (sign-in / create-account before an anonymous user can quote).
  const [authenticateOpen, setAuthenticateOpen] = useState(false);
  const authenticateResolverRef = useRef<
    ((authenticated: boolean) => void) | undefined
  >(undefined);

  const quotePreparationMessage = useBookShipmentRotatingMessage(
    QUOTE_PREPARATION_MESSAGES,
    submittingQuotes,
  );

  function authenticate(): Promise<boolean> {
    return new Promise((resolve) => {
      authenticateResolverRef.current = resolve;
      setAuthenticateOpen(true);
    });
  }

  function resolveAuthenticate(authenticated: boolean) {
    setAuthenticateOpen(false);
    authenticateResolverRef.current?.(authenticated);
    authenticateResolverRef.current = undefined;
  }

  async function onGetQuotes() {
    if (!isValid) {
      onValidationFailed();
      // Release the guard/spinner: if we reach here the shipment never fires
      setSubmittingQuotes(false);
      isSubmittingQuotesRef.current = false;
      return;
    }

    setSubmittingQuotes(true);

    const newShipmentId = uuidv4();

    try {
      // Create shipment and start getting quotes
      await shipmentService.createShipment(
        newShipmentId,
        shipment.pickupDate,
        shipment.pickupLocation,
        shipment.deliveryLocation,
        shipment.pickupContact,
        shipment.deliveryContact,
        shipment.lineItems.map(roundDimensions),
        shipment.pickupReferenceNumber,
        shipment.deliveryReferenceNumber,
        shipment.deliveryAmazonReferenceId,
        shipment.deliveryAmazonShipmentId,
        shipment.addInsuranceToShipment,
        shipment.insuranceAmount,
        shipment.insuranceCurrency,

        shipment.branchId,
        shipment.deliveryDeadline,
        shipment.pickupDeadline,
        shipment.stateOfGoods ?? StateOfGoods.New,

        shipment.pickupLocationHasDock ??
          LoadingCapabilitiesAvailability.Unknown,
        shipment.pickupLocationHasForklift ??
          LoadingCapabilitiesAvailability.Unknown,
        shipment.deliveryLocationHasDock ??
          LoadingCapabilitiesAvailability.Unknown,
        shipment.deliveryLocationHasForklift ??
          LoadingCapabilitiesAvailability.Unknown,

        shipmentDraftId,
        replacesId,
      );

      setSubmittingQuotes(false);

      dispatch(linkShipmentDraft(newShipmentId));

      // Navigate to view the quotes
      // I think this double navigate is to get back button functionality working correctly
      navigate(`/book/details?shipmentId=${newShipmentId}`, {
        replace: true,
      });
      navigate(`/book/quotes?shipmentId=${newShipmentId}`);
    } catch (error) {
      /*
       * Stops spinning animation
       */
      setSubmittingQuotes(false);
      /*
       * Set quotes requested to false because there was an error when requesting
       * If this is not change to `false` the button won't send a request for quoting
       */
      setQuotesRequested(false);
      setQuoteRequestError(true);
      // Release the guard so the user can retry after the error
      isSubmittingQuotesRef.current = false;

      console.error("Error requesting quotes:", error);
    }
  }

  useEffect(
    function () {
      localStorage.removeItem("requotedWithLiftGate");

      if (quotesRequested && isAuthenticated) {
        onGetQuotes();
      }
    },

    [quotesRequested, isAuthenticated],
  );

  async function onGetQuotesOrCreateAccount() {
    // Ignore repeated clicks while a request is already in flight
    if (isSubmittingQuotesRef.current) {
      return;
    }

    setQuoteRequestError(false);
    if (!isValid) {
      onValidationFailed();
      return;
    }

    isSubmittingQuotesRef.current = true;

    if (!isAuthenticated) {
      setShowingCreateAccountPopup(true);
      const isNowAuthenticated = await authenticate();
      setShowingCreateAccountPopup(false);

      if (!isNowAuthenticated) {
        // The user closed the modal without authenticating: allow retry
        isSubmittingQuotesRef.current = false;
        return;
      }
    }

    // Immediate visual feedback: the spinner shows from this render, without
    // waiting for the useEffect cycle that invokes onGetQuotes
    setSubmittingQuotes(true);
    setQuotesRequested(true);
  }

  const dialogs: ReactNode = (
    <Dialog
      open={authenticateOpen}
      onOpenChange={(open) => {
        // Closing without authenticating counts as cancel: allow retry.
        if (!open) resolveAuthenticate(false);
      }}
      // Sized to the pane rather than to a token: the pane is the same fixed
      // 688px form column the standalone sign-in / create-account screens use,
      // and `medium` (720px, minus the dialog body's 32px each side) left it
      // 32px short of fitting. `auto` keeps the two in step if that width ever
      // changes.
      width="auto"
    >
      <ControlPresentationProvider presentation="underlined">
        {authenticateOpen && (
          <AuthenticateForQuotesDialogBody
            onDone={() => resolveAuthenticate(true)}
            onCancel={() => resolveAuthenticate(false)}
          />
        )}
      </ControlPresentationProvider>
    </Dialog>
  );

  return {
    submittingQuotes,
    quoteRequestError,
    quotePreparationMessage,
    showingCreateAccountPopup,
    onGetQuotesOrCreateAccount,
    dialogs,
  };
}
